/**
 * Bodies as evidence. A corpse lying in the open is the loudest thing in a
 * stealth building: any guard that LAYS EYES on one raises the alarm, marks the
 * spot for a search and radios it out — the same escalation the blood tracker
 * runs when a trail ends at a body (`bloodTrail.ts`), but triggered by sight
 * instead of by following blood.
 *
 * "Lays eyes on" reuses the perception the awareness pass already computed this
 * tick: the body must be in the guard's vision cone (`guard.fovTiles`) and lit
 * enough to make out — or close enough to trip over. A body STASHED in a prop
 * (`guard.stashed`, prop verb 'stash') is out of the world and never spotted.
 *
 * Each guard remembers the bodies it has already reported (`guard.knownCorpses`)
 * so a corpse in a patrol lane raises the alarm once, not every tick.
 *
 * Headless — no PixiJS. See CLAUDE.md architecture rule 4.
 */

import { CONFIG } from '../config';
import { broadcastCaution } from './awareness';
import type { Guard } from './guard';
import { perceivedLight, visibilityFactor } from './light';
import type { MapData } from './map';

/** A body a guard just noticed — the caller turns it into a log line. */
export interface CorpseSighting {
  /** The guard that spotted it. */
  guard: Guard;
  /** Entity id of the body. */
  bodyId: number;
  x: number;
  y: number;
}

/**
 * Run after the awareness pass (it reads the cones that pass computed). Returns
 * every FIRST-TIME sighting this tick; each one has already been applied to the
 * spotter (cautious + investigation + broadcast).
 */
export function updateCorpseWatch(guards: readonly Guard[], map: MapData): CorpseSighting[] {
  const bodies = guards.filter((g) => g.body.dead && !g.stashed);
  if (bodies.length === 0) return [];
  const cautious = CONFIG.awareness.thresholds.cautious;
  const out: CorpseSighting[] = [];
  for (const g of guards) {
    if (g.body.dead) continue;
    for (const b of bodies) {
      if (g.knownCorpses.has(b.id)) continue;
      if (!canSeeCorpse(g, b, map)) continue;
      g.knownCorpses.add(b.id);
      if (g.awareness < cautious) g.awareness = cautious;
      if (g.state === 'unaware' || g.state === 'suspicious') g.state = 'cautious';
      g.investigationX = b.x;
      g.investigationY = b.y;
      g.alertingTimer = Math.max(g.alertingTimer, 1.5);
      broadcastCaution(g, guards, CONFIG.awareness.broadcastRadius);
      out.push({ guard: g, bodyId: b.id, x: b.x, y: b.y });
    }
  }
  return out;
}

/** In the cone and lit enough to read as a body — or right underfoot. */
function canSeeCorpse(guard: Guard, body: Guard, map: MapData): boolean {
  const cheb = Math.max(Math.abs(body.x - guard.x), Math.abs(body.y - guard.y));
  if (cheb <= 1) return true; // you don't miss a body at your feet, lit or not
  if (!guard.fovTiles?.has(body.y * map.width + body.x)) return false;
  return visibilityFactor(perceivedLight(map, body.x, body.y, guard.nightVision)) > 0;
}
